import { MusicAPIIntegrationID } from '../music-services/ids';
import { defaultPresaveServices } from './presave';

/**
 * Pre-save Subscriptions (spec docs/superpowers/specs/2026-10-10-music24-presave-subscriptions-design.md).
 * A fan opts in one time and gets every new release of the artist in the library.
 */

/** The id of the opt-in copy that the fan saw. A copy change gets a new id. */
export const PRESAVE_SUBSCRIPTION_CONSENT_VERSION = '2026-10-v1';
/** At most this many release days of one artist reach a fan's library in the window (D10). */
export const PRESAVE_SUBSCRIPTION_RELEASE_CAP = 2;
export const PRESAVE_SUBSCRIPTION_CAP_WINDOW_DAYS = 7;
/** A catalog release older than this does not go to the subscribers (§8.2). */
export const PRESAVE_FAN_RELEASE_MAX_AGE_DAYS = 14;
/** The daily job re-runs a release for this many days, then marks it done (§8.3). */
export const PRESAVE_RELEASE_RETRY_DAYS = 3;
/** At most one reconnect email per fan in this many days (D8). */
export const PRESAVE_RECONNECT_EMAIL_GAP_DAYS = 30;
/** The reconnect email goes this many days before a token ends (Apple Music). */
export const PRESAVE_TOKEN_EXPIRY_WARNING_DAYS = 14;

/** The services whose library musicapi can write. YouTube Music is email-only (D13). */
export const presaveLibraryWriteServices: readonly string[] = defaultPresaveServices;

export function canWriteLibrary(integrationType: string | null): boolean {
  return integrationType !== null && presaveLibraryWriteServices.includes(integrationType);
}

const TRACK_URL: Record<string, (id: string) => string> = {
  [MusicAPIIntegrationID.Spotify]: (id) => `https://open.spotify.com/track/${id}`,
  [MusicAPIIntegrationID.AppleMusic]: (id) => `https://music.apple.com/song/${id}`,
  [MusicAPIIntegrationID.Deezer]: (id) => `https://www.deezer.com/track/${id}`,
  [MusicAPIIntegrationID.Tidal]: (id) => `https://tidal.com/browse/track/${id}`,
  [MusicAPIIntegrationID.AmazonMusic]: (id) => `https://music.amazon.com/tracks/${id}`,
};

/** The public url of a track id on a pre-save service, or null for a service with no rule. */
export function presaveServiceTrackUrl(integrationType: string, itemId: string): string | null {
  const build = TRACK_URL[integrationType];
  return build === undefined ? null : build(encodeURIComponent(itemId));
}

/** The answer of `POST /public/presave/:campaignUuid/subscribe` gains this (§7.1). */
export interface PresaveArtistOptIn {
  available: boolean;
  /** A signed handle for `subscribe-artist`, 30 minutes. Absent when `available` is false. */
  token?: string;
  artistName?: string;
}

/** The stored data of a `presaveSubscribe` block. */
// A type alias, not an interface: Prisma's InputJsonValue accepts only types with an index signature.
export type PresaveSubscribeWidgetData = {
  /** MusicAPIIntegrationID values to offer. YouTube Music gets the email path. */
  services: string[];
};

/** The serialized data of a `presaveSubscribe` block on a public page. */
export interface SerializedPresaveSubscribeWidgetData extends PresaveSubscribeWidgetData {
  artistName: string;
  /** False when the artist's plan has no `canUsePresave`: the public page hides the block. */
  available: boolean;
  serviceAuthEnabled: boolean;
}

/** `GET /accounts/:accountId/fans/summary` */
export interface PresaveFansSummary {
  days: number;
  historyDaysLimit: number;
  total: number;
  byService: { integrationType: string; count: number }[];
  emailOnly: number;
  needsReauth: number;
  newInPeriod: number;
  series: { date: string; subscribed: number; unsubscribed: number }[];
  canUsePresave: boolean;
}

export interface PresaveFansReleaseCounts {
  saved: number;
  emailed: number;
  skippedCap: number;
  skippedPlan: number;
  skippedNoItem: number;
  needsReauth: number;
  failed: number;
}

export interface PresaveFansRelease {
  uuid: string;
  source: 'campaign' | 'catalog';
  title: string;
  /** YYYY-MM-DD */
  releaseDay: string;
  status: 'delivering' | 'done';
  counts: PresaveFansReleaseCounts;
}

/** `GET /accounts/:accountId/fans/releases` */
export interface PresaveFansReleasesPage {
  releases: PresaveFansRelease[];
  nextCursor: string | null;
}

/** `ReleaseOverview.fanRelease` (§8.2). Null when the song has a pre-save campaign. */
export interface PresaveFanReleaseState {
  eligible: boolean;
  /** Why not eligible; null when eligible or already sent. */
  reason: 'notReleased' | 'tooOld' | 'noServiceIds' | 'plan' | null;
  subscriberCount: number;
  /** Set when the release was sent. */
  releaseUuid: string | null;
}
